// Central registry of Complete Chapter Notes.
// Add new chapter entries here. Each chapter is a separate module that
// exports a structured JS object consumed by ChapterNotes viewer.

import quadraticEquations from './mathematics/quadraticEquations';
import complexNumbers from './mathematics/complexNumbers';
import sequencesAndSeries from './mathematics/sequencesAndSeries';
import binomialTheorem from './mathematics/binomialTheorem';
import permutationsCombinations from './mathematics/permutationsCombinations';
import trigonometricRatiosIdentities from './mathematics/trigonometricRatiosIdentities';
import setsRelationsFunctions from './mathematics/setsRelationsFunctions';
import straightLines from './mathematics/straightLines';
import circles from './mathematics/circles';
import conicSections from './mathematics/conicSections';
import limitsContinuityDifferentiability from './mathematics/limitsContinuityDifferentiability';
import differentiation from './mathematics/differentiation';
import applicationsOfDerivatives from './mathematics/applicationsOfDerivatives';
import indefiniteIntegration from './mathematics/indefiniteIntegration';
import definiteIntegrationAreas from './mathematics/definiteIntegrationAreas';
import differentialEquations from './mathematics/differentialEquations';
import matrices from './mathematics/matrices';
import determinants from './mathematics/determinants';
import vectors from './mathematics/vectors';
import threeDGeometry from './mathematics/threeDGeometry';
import probability from './mathematics/probability';
import statisticsReasoning from './mathematics/statisticsReasoning';
import logarithms from './mathematics/logarithms';
import inverseTrigonometricFunctions from './mathematics/inverseTrigonometricFunctions';
import hyperbolicFunctions from './mathematics/hyperbolicFunctions';
import solutionOfTriangles from './mathematics/solutionOfTriangles';
import mathematicalInduction from './mathematics/mathematicalInduction';
import linearProgramming from './mathematics/linearProgramming';

import unitsMeasurements from './physics/unitsMeasurements';
import kinematics from './physics/kinematics';
import lawsOfMotion from './physics/lawsOfMotion';
import workEnergyPower from './physics/workEnergyPower';
import rotationalMotion from './physics/rotationalMotion';
import gravitation from './physics/gravitation';
import propertiesOfMatter from './physics/propertiesOfMatter';
import thermodynamics from './physics/thermodynamics';
import oscillationsWaves from './physics/oscillationsWaves';
import electrostatics from './physics/electrostatics';
import capacitance from './physics/capacitance';
import currentElectricity from './physics/currentElectricity';
import movingChargesMagnetism from './physics/movingChargesMagnetism';
import magnetismMatter from './physics/magnetismMatter';
import emiAc from './physics/emiAc';
import emWaves from './physics/emWaves';
import rayOptics from './physics/rayOptics';
import waveOptics from './physics/waveOptics';
import dualNature from './physics/dualNature';
import atoms from './physics/atoms';
import nuclei from './physics/nuclei';
import semiconductorsDiodes from './physics/semiconductorsDiodes';
import transistorsLogicGates from './physics/transistorsLogicGates';
import communicationSystems from './physics/communicationSystems';
import centreOfMassCollisions from './physics/centreOfMassCollisions';
import fluidDynamics from './physics/fluidDynamics';

import basicConcepts from './chemistry/basicConcepts';
import atomicStructure from './chemistry/atomicStructure';
import chemicalBonding from './chemistry/chemicalBonding';
import statesOfMatter from './chemistry/statesOfMatter';
import thermodynamicsChem from './chemistry/thermodynamicsChem';
import equilibrium from './chemistry/equilibrium';
import periodicClassification from './chemistry/periodicClassification';
import redoxReactions from './chemistry/redoxReactions';
import hydrogen from './chemistry/hydrogen';
import sBlock from './chemistry/sBlock';
import pBlock13 from './chemistry/pBlock13';
import pBlock14 from './chemistry/pBlock14';
import solidState from './chemistry/solidState';
import solutions from './chemistry/solutions';
import electrochemistry from './chemistry/electrochemistry';
import chemicalKinetics from './chemistry/chemicalKinetics';
import surfaceChemistry from './chemistry/surfaceChemistry';
import metallurgy from './chemistry/metallurgy';
import pBlock15 from './chemistry/pBlock15';
import pBlock16 from './chemistry/pBlock16';
import pBlock17 from './chemistry/pBlock17';
import dAndFBlock from './chemistry/dAndFBlock';
import coordinationCompounds from './chemistry/coordinationCompounds';
import haloalkanesHaloarenes from './chemistry/haloalkanesHaloarenes';
import alcoholsPhenolsEthers from './chemistry/alcoholsPhenolsEthers';
import aldehydesKetonesAcids from './chemistry/aldehydesKetonesAcids';
import amines from './chemistry/amines';
import biomolecules from './chemistry/biomolecules';
import polymers from './chemistry/polymers';
import chemistryEverydayLife from './chemistry/chemistryEverydayLife';
import generalOrganicChemistry from './chemistry/generalOrganicChemistry';
import hydrocarbons from './chemistry/hydrocarbons';
import purificationOrganicCompounds from './chemistry/purificationOrganicCompounds';
import environmentalChemistry from './chemistry/environmentalChemistry';

import biologicalClassification from './biology/biologicalClassification';
import cellUnitOfLife from './biology/cellUnitOfLife';
import cellCycleDivision from './biology/cellCycleDivision';
import principlesOfInheritance from './biology/principlesOfInheritance';
import molecularBasisInheritance from './biology/molecularBasisInheritance';
import evolution from './biology/evolution';
import digestionAbsorption from './biology/digestionAbsorption';
import breathingExchangeGases from './biology/breathingExchangeGases';
import bodyFluidsCirculation from './biology/bodyFluidsCirculation';
import excretoryProducts from './biology/excretoryProducts';
import locomotionMovement from './biology/locomotionMovement';
import neuralControlCoordination from './biology/neuralControlCoordination';
import organismsPopulations from './biology/organismsPopulations';
import ecosystem from './biology/ecosystem';
import biodiversityConservation from './biology/biodiversityConservation';
import sexualReproductionFloweringPlants from './biology/sexualReproductionFloweringPlants';
import humanReproduction from './biology/humanReproduction';
import reproductiveHealth from './biology/reproductiveHealth';
import photosynthesisHigherPlants from './biology/photosynthesisHigherPlants';
import respirationPlants from './biology/respirationPlants';
import plantGrowthDevelopment from './biology/plantGrowthDevelopment';
import biotechPrinciplesProcesses from './biology/biotechPrinciplesProcesses';
import biotechApplications from './biology/biotechApplications';
import microbesHumanWelfare from './biology/microbesHumanWelfare';
import humanHealthDisease from './biology/humanHealthDisease';
import strategiesFoodProduction from './biology/strategiesFoodProduction';
import environmentalIssues from './biology/environmentalIssues';
import livingWorld from './biology/livingWorld';
import plantKingdom from './biology/plantKingdom';
import animalKingdom from './biology/animalKingdom';
import morphologyFloweringPlants from './biology/morphologyFloweringPlants';
import anatomyFloweringPlants from './biology/anatomyFloweringPlants';
import structuralOrganisationAnimals from './biology/structuralOrganisationAnimals';
import transportInPlants from './biology/transportInPlants';
import mineralNutrition from './biology/mineralNutrition';
import chemicalCoordinationIntegration from './biology/chemicalCoordinationIntegration';
import biomoleculesClass11 from './biology/biomoleculesClass11';

// Subjects metadata (used by index page)
export const SUBJECTS = [
  {
    id: 'mathematics',
    label: 'Mathematics',
    exam: 'JEE',
    color: '#ff8a3d',
    accent: '#ffe6d3',
    icon: '📐',
    description:
      'Complete JEE Main + Advanced notes — derivations, formulas, graphs, examples and PYQs.',
  },
  {
    id: 'physics',
    label: 'Physics',
    exam: 'JEE / NEET',
    color: '#3b82f6',
    accent: '#dbeafe',
    icon: '⚛️',
    description: 'Chapter-wise notes with figures, derivations and PYQs for JEE & NEET.',
  },
  {
    id: 'chemistry',
    label: 'Chemistry',
    exam: 'JEE / NEET',
    color: '#10b981',
    accent: '#d1fae5',
    icon: '⚗️',
    description: 'Complete JEE Main + NEET notes — derivations, mechanisms and PYQs.',
  },
  {
    id: 'biology',
    label: 'Biology',
    exam: 'NEET',
    color: '#a855f7',
    accent: '#ede9fe',
    icon: '🧬',
    description: 'NCERT-aligned Botany & Zoology notes for NEET.',
  },
];

// All chapters keyed by `${subject}/${slug}`
export const CHAPTERS = {
  'mathematics/quadratic-equations': quadraticEquations,
  'mathematics/complex-numbers': complexNumbers,
  'mathematics/sequences-and-series': sequencesAndSeries,
  'mathematics/binomial-theorem': binomialTheorem,
  'mathematics/permutations-combinations': permutationsCombinations,
  'mathematics/trigonometric-ratios-identities': trigonometricRatiosIdentities,
  'mathematics/sets-relations-functions': setsRelationsFunctions,
  'mathematics/straight-lines': straightLines,
  'mathematics/circles': circles,
  'mathematics/conic-sections': conicSections,
  'mathematics/limits-continuity-differentiability': limitsContinuityDifferentiability,
  'mathematics/differentiation': differentiation,
  'mathematics/applications-of-derivatives': applicationsOfDerivatives,
  'mathematics/indefinite-integration': indefiniteIntegration,
  'mathematics/definite-integration-areas': definiteIntegrationAreas,
  'mathematics/differential-equations': differentialEquations,
  'mathematics/matrices': matrices,
  'mathematics/determinants': determinants,
  'mathematics/vectors': vectors,
  'mathematics/three-d-geometry': threeDGeometry,
  'mathematics/probability': probability,
  'mathematics/statistics-reasoning': statisticsReasoning,
  'mathematics/logarithms': logarithms,
  'mathematics/inverse-trigonometric-functions': inverseTrigonometricFunctions,
  'mathematics/hyperbolic-functions': hyperbolicFunctions,
  'mathematics/solution-of-triangles': solutionOfTriangles,
  'mathematics/mathematical-induction': mathematicalInduction,
  'mathematics/linear-programming': linearProgramming,
  'physics/units-measurements': unitsMeasurements,
  'physics/kinematics': kinematics,
  'physics/laws-of-motion': lawsOfMotion,
  'physics/work-energy-power': workEnergyPower,
  'physics/rotational-motion': rotationalMotion,
  'physics/gravitation': gravitation,
  'physics/properties-of-matter': propertiesOfMatter,
  'physics/thermodynamics': thermodynamics,
  'physics/oscillations-waves': oscillationsWaves,
  'physics/electrostatics': electrostatics,
  'physics/capacitance': capacitance,
  'physics/current-electricity': currentElectricity,
  'physics/moving-charges-magnetism': movingChargesMagnetism,
  'physics/magnetism-matter': magnetismMatter,
  'physics/emi-ac': emiAc,
  'physics/em-waves': emWaves,
  'physics/ray-optics': rayOptics,
  'physics/wave-optics': waveOptics,
  'physics/dual-nature': dualNature,
  'physics/atoms': atoms,
  'physics/nuclei': nuclei,
  'physics/semiconductors-diodes': semiconductorsDiodes,
  'physics/transistors-logic-gates': transistorsLogicGates,
  'physics/communication-systems': communicationSystems,
  'physics/centre-of-mass-collisions': centreOfMassCollisions,
  'physics/fluid-dynamics': fluidDynamics,
  'chemistry/basic-concepts': basicConcepts,
  'chemistry/atomic-structure': atomicStructure,
  'chemistry/chemical-bonding': chemicalBonding,
  'chemistry/states-of-matter': statesOfMatter,
  'chemistry/thermodynamics-chem': thermodynamicsChem,
  'chemistry/equilibrium': equilibrium,
  'chemistry/periodic-classification': periodicClassification,
  'chemistry/redox-reactions': redoxReactions,
  'chemistry/hydrogen': hydrogen,
  'chemistry/s-block': sBlock,
  'chemistry/p-block-13': pBlock13,
  'chemistry/p-block-14': pBlock14,
  'chemistry/solid-state': solidState,
  'chemistry/solutions': solutions,
  'chemistry/electrochemistry': electrochemistry,
  'chemistry/chemical-kinetics': chemicalKinetics,
  'chemistry/surface-chemistry': surfaceChemistry,
  'chemistry/metallurgy': metallurgy,
  'chemistry/p-block-15': pBlock15,
  'chemistry/p-block-16': pBlock16,
  'chemistry/p-block-17': pBlock17,
  'chemistry/d-and-f-block': dAndFBlock,
  'chemistry/coordination-compounds': coordinationCompounds,
  'chemistry/haloalkanes-haloarenes': haloalkanesHaloarenes,
  'chemistry/alcohols-phenols-ethers': alcoholsPhenolsEthers,
  'chemistry/aldehydes-ketones-acids': aldehydesKetonesAcids,
  'chemistry/amines': amines,
  'chemistry/biomolecules': biomolecules,
  'chemistry/polymers': polymers,
  'chemistry/chemistry-everyday-life': chemistryEverydayLife,
  'chemistry/general-organic-chemistry': generalOrganicChemistry,
  'chemistry/hydrocarbons': hydrocarbons,
  'chemistry/purification-organic-compounds': purificationOrganicCompounds,
  'chemistry/environmental-chemistry': environmentalChemistry,
  'biology/biological-classification': biologicalClassification,
  'biology/cell-unit-of-life': cellUnitOfLife,
  'biology/cell-cycle-division': cellCycleDivision,
  'biology/principles-of-inheritance': principlesOfInheritance,
  'biology/molecular-basis-inheritance': molecularBasisInheritance,
  'biology/evolution': evolution,
  'biology/digestion-absorption': digestionAbsorption,
  'biology/breathing-exchange-gases': breathingExchangeGases,
  'biology/body-fluids-circulation': bodyFluidsCirculation,
  'biology/excretory-products': excretoryProducts,
  'biology/locomotion-movement': locomotionMovement,
  'biology/neural-control-coordination': neuralControlCoordination,
  'biology/organisms-populations': organismsPopulations,
  'biology/ecosystem': ecosystem,
  'biology/biodiversity-conservation': biodiversityConservation,
  'biology/sexual-reproduction-flowering-plants': sexualReproductionFloweringPlants,
  'biology/human-reproduction': humanReproduction,
  'biology/reproductive-health': reproductiveHealth,
  'biology/photosynthesis-higher-plants': photosynthesisHigherPlants,
  'biology/respiration-plants': respirationPlants,
  'biology/plant-growth-development': plantGrowthDevelopment,
  'biology/biotech-principles-processes': biotechPrinciplesProcesses,
  'biology/biotech-applications': biotechApplications,
  'biology/microbes-human-welfare': microbesHumanWelfare,
  'biology/human-health-disease': humanHealthDisease,
  'biology/strategies-food-production': strategiesFoodProduction,
  'biology/environmental-issues': environmentalIssues,
  'biology/living-world': livingWorld,
  'biology/plant-kingdom': plantKingdom,
  'biology/animal-kingdom': animalKingdom,
  'biology/morphology-flowering-plants': morphologyFloweringPlants,
  'biology/anatomy-flowering-plants': anatomyFloweringPlants,
  'biology/structural-organisation-animals': structuralOrganisationAnimals,
  'biology/transport-in-plants': transportInPlants,
  'biology/mineral-nutrition': mineralNutrition,
  'biology/chemical-coordination-integration': chemicalCoordinationIntegration,
  'biology/biomolecules-class11': biomoleculesClass11,
};

// Chapters grouped per subject (for the index UI)
export const CHAPTERS_BY_SUBJECT = Object.values(CHAPTERS).reduce((acc, ch) => {
  (acc[ch.subject] = acc[ch.subject] || []).push(ch);
  return acc;
}, {});

export function getChapter(subject, slug) {
  return CHAPTERS[`${subject}/${slug}`];
}
